import React, { useCallback } from 'react';
import classNames from 'classnames';
import styles from './Editor.module.scss';
import EditorLayoutWrapper from './components/EditorLayoutWrapper/EditorLayoutWrapper';
import EditorLayout from './components/EditorLayout/EditorLayout';
import { Phrases } from '../../utilities/constants/phrases';
import { IconGlyph } from '../../components/Icon';
import {
  ButtonAppearance,
  ButtonSize,
  IconPosition,
} from '../../components/ButtonBase/types';
import PageLayout from '../../components/PageLayout/PageLayout';
import { useEditorContext } from '../../hooks/editor/useEditorContext';
import { useEditorGeneration } from '../../hooks/editor/useEditorGeneration';
import { useEditorNavigation } from '../../hooks/editor/useEditorNavigation';
import { useAuthContext } from '../../hooks/auth/useAuthContext';
import AdsContainer from '../../components/AdsContainer/AdsContainer';
import { useApp } from '../../hooks/state/useApp';
import Button from '../../components/Button/Button';
import { AdsBannerSlots } from '../../utilities/constants/common';
import { EditorPopupId } from '../../utilities/constants/popup-type';
import { useEditorPopups } from '../../hooks/editor/useEditorPopups';
import PopupGenOnePremiumAIResponse from '../../components/Popup/PopupGenOnePremiumAIResponse/PopupGenOnePremiumAIResponse';
import PopupPremiumAnswerUsed from '../../components/Popup/PopupPremiumAnswerUsed/PopupPremiumAnswerUsed';
import PopupGoogleAdsense from '../../components/Popup/PopupGoogleAdsense/PopupGoogleAdsense';
import PopupGenFreeAiResponse from '../../components/Popup/PopupGenFreeAIResponse/PopupGenFreeAIResponse';
import PopupGenPremiumAiResponse from '../../components/Popup/PopupGenPremiumAIResponse/PopupGenPremiumAIResponse';
import { useEditorGenerationOnRender } from '../../hooks/editor/useEditorGenerationOnRender';
import { useOpenSubscriptionPopup } from '../../hooks/popup/useOpenSubscriptionPopup';
import { useEditorToast } from '../../hooks/editor/useEditorToast';

// script set on parent domain
const WIDGET_ID = 'widget-container';

const Editor: React.FC = () => {
  const { isUserSubscribed, freePremiumGenerationsAttemptsCount } =
    useAuthContext();
  const { EditorActionCreator, editorDispatch } = useEditorContext();

  const { isDesktop } = useApp();
  const { handleOpenSubscriptionPopup } = useOpenSubscriptionPopup();

  const {
    text,
    title,
    subjectGroup,
    isTextGenerating,
    handleGenerationRequest,
    error: generationError,
  } = useEditorGeneration();

  const { handleCreateNewQuestion } = useEditorNavigation();
  const {
    isOpenPopups,
    justUsedFreeSmartAnswer,
    handleSetIsOpenPopup,
    handleInitEditorPopups,
  } = useEditorPopups();

  useEditorGenerationOnRender({
    handleGenerationRequest,
    handleInitEditorPopups,
  });

  useEditorToast({
    error:
      !isOpenPopups.genOnePremiumAiResponse &&
      !isOpenPopups.genPremiumAiResponse &&
      !isOpenPopups.genFreeAiResponse
        ? generationError
        : null,
  });

  const isPremium =
    isUserSubscribed ||
    freePremiumGenerationsAttemptsCount > 0 ||
    (freePremiumGenerationsAttemptsCount === 0 && justUsedFreeSmartAnswer);
  const showAds = !isPremium;
  const showDesktopAds = showAds && isDesktop;
  const showMobileAds = showAds && !isDesktop;

  const onAiInputSubmit = useCallback(
    (value: string) => {
      editorDispatch(EditorActionCreator.setUserHomeworkHelpNextRequest(value));

      if (!isUserSubscribed && justUsedFreeSmartAnswer) {
        handleSetIsOpenPopup(EditorPopupId.PREMIUM_ANSWER_USED, true);
        return;
      }

      if (!isUserSubscribed) {
        handleOpenSubscriptionPopup();
        return;
      }

      handleSetIsOpenPopup(EditorPopupId.GEN_PREMIUM_AI_RESPONSE, true);
      handleGenerationRequest(value);
    },
    [
      EditorActionCreator,
      editorDispatch,
      handleGenerationRequest,
      justUsedFreeSmartAnswer,
      handleSetIsOpenPopup,
      isUserSubscribed,
      handleOpenSubscriptionPopup,
    ]
  );

  if (isOpenPopups.genOnePremiumAiResponse) {
    return (
      <PopupGenOnePremiumAIResponse
        isOpen
        isLoading={isTextGenerating}
        onClose={() =>
          handleSetIsOpenPopup(EditorPopupId.GEN_ONE_PREMIUM_AI_RESPONSE, false)
        }
      />
    );
  }

  if (isOpenPopups.googleAdsense) {
    return (
      <PopupGoogleAdsense
        isOpen
        onClose={() => {
          handleSetIsOpenPopup(EditorPopupId.GOOGLE_ADSENSE, false);
          handleSetIsOpenPopup(EditorPopupId.GEN_FREE_AI_RESPONSE, true);
          handleGenerationRequest();
        }}
      />
    );
  }

  if (isOpenPopups.genFreeAiResponse) {
    return (
      <PopupGenFreeAiResponse
        isOpen
        isLoading={isTextGenerating}
        onClose={() =>
          handleSetIsOpenPopup(EditorPopupId.GEN_FREE_AI_RESPONSE, false)
        }
      />
    );
  }

  if (isOpenPopups.genPremiumAiResponse) {
    return (
      <PopupGenPremiumAiResponse
        isOpen
        isLoading={isTextGenerating}
        onClose={() =>
          handleSetIsOpenPopup(EditorPopupId.GEN_PREMIUM_AI_RESPONSE, false)
        }
      />
    );
  }

  return (
    <PageLayout>
      <PopupPremiumAnswerUsed
        isOpen={isOpenPopups.premiumAnswerUsed}
        onSkipClick={() => {
          handleSetIsOpenPopup(EditorPopupId.PREMIUM_ANSWER_USED, false);
          handleSetIsOpenPopup(EditorPopupId.GOOGLE_ADSENSE, true);
        }}
      />

      <main
        className={classNames(styles.layout, {
          [styles.premium]: isPremium,
        })}>
        <div className={styles.header}>
          <div className={styles.container}>
            {showAds ? (
              <div className={styles.middleBarContent}>
                {showDesktopAds && (
                  <AdsContainer BannerProps={{ ...AdsBannerSlots.SLOT_3 }} />
                )}
                {showMobileAds && (
                  <AdsContainer BannerProps={{ ...AdsBannerSlots.SLOT_4 }} />
                )}
              </div>
            ) : null}
          </div>
        </div>
        <div className={styles.rightSidebar}>
          {showDesktopAds ? (
            <div className={styles.sidebarContent}>
              <AdsContainer BannerProps={{ ...AdsBannerSlots.SLOT_2 }} />
            </div>
          ) : null}
        </div>
        <div className={classNames(styles.footer, styles.container)}>
          {showMobileAds ? (
            <div className={styles.middleBarContent}>
              {showDesktopAds && (
                <AdsContainer BannerProps={{ ...AdsBannerSlots.SLOT_3 }} />
              )}
              {showMobileAds && (
                <AdsContainer BannerProps={{ ...AdsBannerSlots.SLOT_4 }} />
              )}
            </div>
          ) : null}
        </div>
        <div className={styles.leftSidebar}>
          {showDesktopAds ? (
            <div className={styles.sidebarContent}>
              <AdsContainer BannerProps={{ ...AdsBannerSlots.SLOT_1 }} />
            </div>
          ) : null}
        </div>
        <div className={styles.editorWrapper}>
          <div className={classNames(styles.editor, styles.container)}>
            <div className='d-flex gap-xxxs flex-jc-sb flex-ai-c'>
              <h2 className='text-xl text-xxxl@md fw-bold c-main'>
                {Phrases.YOUR_SOLUTION_TITLE}
              </h2>
              <div className={styles.newButton}>
                <Button
                  onClick={handleCreateNewQuestion}
                  disabled={isTextGenerating}
                  appearance={ButtonAppearance.WHITE}
                  squared
                  icon={{
                    position: IconPosition.RIGHT,
                    glyph: IconGlyph.FORWARD,
                  }}
                  size={ButtonSize.SMALL}>
                  {Phrases.NEW_QUESTION_BUTTON_LABEL}
                </Button>
              </div>
            </div>
            <EditorLayoutWrapper>
              <EditorLayout
                isTextGenerating={isTextGenerating}
                isPremium={isPremium}
                isUserSubscribed={isUserSubscribed}
                text={text}
                title={title}
                subjectGroup={subjectGroup}
                onAiInputSubmit={onAiInputSubmit}
              />
            </EditorLayoutWrapper>
          </div>
        </div>

        <div
          id={WIDGET_ID}
          className={styles.widgetContainer}
        />
      </main>
    </PageLayout>
  );
};

export default Editor;
